import React from 'react';
import './MapLayerToolbar.css';

const MapLayerToolbar = ({ activeLayer, productParam }) => {
  // Define layer display names and corresponding color key types
  const layerInfo = {
    radar: { 
      displayName: 'Radar', 
      keyType: 'multi-key',
      labels: ['Rain', 'Mix', 'Snow'],
      intensityLabels: ['Light', 'Heavy'],
      showScale: true
    },
    radar_satellite: { 
      displayName: 'Radar & Satellite', 
      keyType: 'multi-key',
      labels: ['Rain', 'Mix', 'Snow'],
      intensityLabels: ['Light', 'Heavy'],
      showScale: true
    },
    satellite: { 
      displayName: 'Satellite', 
      keyType: 'clouds-key',
      labels: [],
      showScale: false
    },
    precipitation: { 
      displayName: 'Precipitation', 
      keyType: 'rain-key',
      labels: ['Light', 'Heavy'],
      showScale: true
    },
    snow: { 
      displayName: 'Snow Forecast', 
      keyType: 'snow-key',
      labels: ['Light', 'Heavy'],
      showScale: true
    },
    temp: { 
      displayName: 'Temperature', 
      keyType: 'temp-key',
      labels: ['-70°', '-50°', '-30°', '-10°', '0°', '10°', '20°', '30°', '40°', '50°', '60°', '70°', '80°', '90°', '100°', '110°', '120°', '130°'],
      showScale: true,
      showFull: true
    },
    temp_feels_like: { 
      displayName: 'Feels Like', 
      keyType: 'feels-like-key',
      labels: ['-50°', '-30°', '-10°', '10°', '30°', '40°', '50°', '80°', '100°', '120°'],
      showScale: true,
      showFull: true
    },
    dew_point: { 
      displayName: 'Dew Point', 
      keyType: 'dewpoint-key',
      labels: ['-40°', '-20°', '0°', '20°', '40°', '60°', '80°+'],
      showScale: true,
      showFull: true
    },
    wind_speed_gusts: { 
      displayName: 'Wind Speed', 
      keyType: 'wind-key',
      labels: ['10', '20', '30', '40', '50', '60+'],
      showScale: true,
      showFull: true
    },
    uv_index: { 
      displayName: 'UV Index', 
      keyType: 'uv-key',
      labels: ['1-2', '3', '4', '5', '6', '7', '8', '9', '10', '11'],
      showScale: true,
      showFull: true
    }
  };

  // If there's no active layer or it's not a weather layer, don't display toolbar
  if (!activeLayer || activeLayer.startsWith('base_') || activeLayer.startsWith('overlay_')) {
    return null;
  }

  // Get layer info or use defaults
  const { 
    displayName = 'Weather Layer', 
    keyType = 'rain-key',
    labels = ['Low', 'High'],
    metricLabels = [],
    showScale = true,
    showFull = false,
    intensityLabels = ['Light', 'Heavy']
  } = layerInfo[activeLayer] || {};

  // Don't show anything for satellite/clouds
  if (keyType === 'clouds-key') {
    return (
      <div className="map-layer-toolbar">
        <div className="map-layer-name">
          {displayName}
        </div>
      </div>
    );
  }

  // Generate segments for the color bar
  const renderColorSegments = () => {
    // For multi-key, we'll return 3 separate color bars
    if (keyType === 'multi-key') {
      return (
        <>
          <div className="multi-key-group">
            <div className="key-type-label">{labels[0]}</div>
            <div className="rain-key-segment"></div>
            <div className="intensity-labels">
              <span className="light-label">{intensityLabels[0]}</span>
              <span className="heavy-label">{intensityLabels[1]}</span>
            </div>
          </div>
          <div className="multi-key-group">
            <div className="key-type-label">{labels[1]}</div>
            <div className="mix-key-segment"></div>
            <div className="intensity-labels">
              <span className="light-label">{intensityLabels[0]}</span>
              <span className="heavy-label">{intensityLabels[1]}</span>
            </div>
          </div>
          <div className="multi-key-group">
            <div className="key-type-label">{labels[2]}</div>
            <div className="snow-key-segment"></div>
            <div className="intensity-labels">
              <span className="light-label">{intensityLabels[0]}</span>
              <span className="heavy-label">{intensityLabels[1]}</span>
            </div>
          </div>
        </>
      );
    }
    
    // For all other keys, we'll use a single gradient div
    return <div className={`${keyType}-gradient`}></div>;
  };

  // Render the key labels
  const renderKeyLabels = () => {
    if (keyType === 'temp-key' && showFull) {
      // Temperature with filtered labels for clarity - show key temperatures and edges
      const labelPositions = [0, 2, 4, 6, 8, 10, 12, 14, 17]; // Show -70, -30, -10, 20, 40, 60, 80, 100, 130
      return (
        <div className="temp-key-labels">
          {labelPositions.map((index) => (
            <span key={index} className="temp-label">{labels[index]}</span>
          ))}
        </div>
      );
    }
    
    if (keyType === 'dewpoint-key' && showFull) {
      // Show all dew point labels for proper alignment
      const labelPositions = [0, 1, 2, 3, 4, 5, 6];
      return (
        <div className="temp-key-labels">
          {labelPositions.map((index) => (
            <span key={index} className="temp-label">{labels[index]}</span>
          ))}
        </div>
      );
    }
    
    if (keyType === 'uv-key' && showFull) {
      // Show all UV index labels
      const labelPositions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      return (
        <div className="temp-key-labels">
          {labelPositions.map((index) => (
            <span key={index} className="temp-label">{labels[index]}</span>
          ))}
        </div>
      );
    }
    
    if (keyType === 'wind-key' && showFull) {
      // Show wind speed labels in 10mph increments
      const labelPositions = [0, 1, 2, 3, 4, 5];
      return (
        <div className="temp-key-labels">
          {labelPositions.map((index) => (
            <span key={index} className="temp-label">{labels[index]}</span>
          ))}
        </div>
      );
    }
    
    if (keyType === 'feels-like-key' && showFull) {
      // Show specific key temperatures for feels like scale
      const labelPositions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]; // All positions for -50 through 120
      return (
        <div className="temp-key-labels">
          {labelPositions.map((index) => (
            <span key={index} className="temp-label">{labels[index]}</span>
          ))}
        </div>
      );
    }
    
    if (keyType === 'multi-key') {
      // For multi-key, we handle the labels in renderColorSegments
      return null;
    }
    
    // Standard case with just Low/High or Light/Heavy labels
    return (
      <div className="key-labels">
        <span>{labels[0]}</span>
        <span>{labels[1]}</span>
      </div>
    );
  };

  return (
    <div className="map-layer-toolbar">
      <div className="map-layer-name">
        {displayName}
      </div>
      {showScale && (
        <div className="map-layer-key">
          <div className={`key-item ${keyType === 'temp-key' ? 'temp-item' : ''} ${keyType === 'multi-key' ? 'multi-item' : ''}`}>
            <div className={`color-bar-container ${keyType}`}>
              {renderColorSegments()}
            </div>
            {renderKeyLabels()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLayerToolbar; 